import type Highcharts from "highcharts";
import type { FailureValue, IndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import type { Project } from "../../../../api/useProjects";
import type { Setup } from "../../../../api/useSetups";
import { comparatorBy } from "diagnostics-lib/comparator";
import { lightest } from "@ui";
import type { FailureImpactMetadata } from "../../Chart/PointMetadata";
import { getFailureColor } from "../../useFailureColor";
import getQuantitySigma from "../../../../api/getQuantitySigma";
import type { IndexedFailureMetadata } from "../../../../api/useFailureMetadata";
import type { Quantity } from "../../../../api/useQuantities";
import { isFaultPromoted } from "../../../../domain/isFaultPromoted";
import { indexBy } from "diagnostics-lib/iterable";
import { FAULT_IMPACT_SERIE } from "../../Chart/series/serieIds";

interface QuantityValues {
  [x: string]: { measured: number; simulated: number | null; std: number | null };
}

function getQuantityFailureImpactsPoints(
  quantities: Quantity[],
  failureValue: FailureValue,
  indexedFailureMetadata: IndexedFailureMetadata,
  normalized: boolean,
  setup: Setup | null,
  quantityValues: QuantityValues,
): Highcharts.PointOptionsObject[] {
  const failuresByName = setup ? indexBy(setup?.failures, "name") : null;
  const failure = failuresByName ? (failuresByName.get(failureValue.failureName) ?? null) : null;
  const failureFilters = failure?.filters ?? [];

  const fvStatus = indexedFailureMetadata.getFailureStatus(
    failureValue.date,
    failureValue.failureName,
  );
  return quantities.map(quantity => {
    const impact = failureValue.impacts[quantity.id];
    const isPromoted = isFaultPromoted(quantityValues, failureFilters);
    if (impact === undefined) {
      const metadata: FailureImpactMetadata = {
        human: {
          value: 0,
          unit: null,
          precision: null,
          sign: null,
        },
        failureStatus: fvStatus,
        confidenceIndicator: null,
        isManual: false,
        isPromoted,
      };
      return {
        name: quantity.name,
        y: 0,
        custom: { ...metadata, failureDisplayName: failure?.displayName },
      };
    } else {
      const sigma = getQuantitySigma(setup, quantity.id);
      const metadata: FailureImpactMetadata = {
        human: {
          value: impact,
          unit: quantity.unit,
          precision: 2,
          sign: null,
        },
        failureStatus: fvStatus,
        confidenceIndicator: failureValue.confidence,
        isManual: false,
        isPromoted,
      };
      return {
        name: quantity.name,
        y: normalized && sigma !== null ? impact / sigma : impact,
        custom: { ...metadata, failureDisplayName: failure?.displayName },
      };
    }
  });
}

const statusWeight = {
  TO_BE_ANALYSED: 0,
  BACK_TO_BE_ANALYSED: 0,
  SUSPECTED: 1,
  CONFIRMED: 2,
  LEGACY_REJECTED: 3,
  REJECTED: 4,
};

function getQuantityFailureImpactsSeries(
  date: number,
  project: Project,
  quantities: Quantity[],
  indexedFailureValues: IndexedFailureValues,
  indexedFailureMetadata: IndexedFailureMetadata,
  normalized: boolean,
  setup: Setup | null,
  quantityValues: QuantityValues,
): Highcharts.SeriesColumnOptions[] {
  const failureValuesAtDate = indexedFailureValues[date];
  const failureValues = [];
  for (const failureName in failureValuesAtDate) {
    const fv = failureValuesAtDate[failureName];
    if (fv === undefined) throw new Error("undefined failure value");
    const status = indexedFailureMetadata.getFailureStatus(date, failureName);
    if (status !== "REJECTED" && status !== "LEGACY_REJECTED") failureValues.push(fv);
  }

  return failureValues
    .sort((a, b) => {
      const aStatus = indexedFailureMetadata.getFailureStatus(a.date, a.failureName);
      const bStatus = indexedFailureMetadata.getFailureStatus(b.date, b.failureName);
      return statusWeight[aStatus] - statusWeight[bStatus];
    })
    .flatMap((failureValue, i) => {
      const data = getQuantityFailureImpactsPoints(
        quantities,
        failureValue,
        indexedFailureMetadata,
        normalized,
        setup,
        quantityValues,
      );
      const color = getFailureColor(project, "FAILURE", failureValue.failureName);

      const fvStatus = indexedFailureMetadata.getFailureStatus(
        failureValue.date,
        failureValue.failureName,
      );

      const failuresByName = setup ? indexBy(setup?.failures, "name") : null;
      const failure = failuresByName
        ? (failuresByName.get(failureValue.failureName) ?? null)
        : null;
      const displayName = failure?.displayName ?? failureValue.failureName;

      return [
        {
          name: failureValue.failureName,
          custom: { failureDisplayName: displayName },
          id: `${FAULT_IMPACT_SERIE}-${failureValue.failureName}`,
          data,
          color: fvStatus === "CONFIRMED" ? color : lightest(color),
          borderColor: color,
          stack: "1",
          type: "column" as const,
          replicateInNavigator: false,
          index: i,
          showInLegend: true,
        },
      ];
    })
    .sort(comparatorBy("name"))
    .map((s, i) => ({ ...s, legendIndex: i + 2 }));
}

export default getQuantityFailureImpactsSeries;
